import { get, flowRight as compose } from 'lodash';
import { Formik } from 'formik';
import { PropTypes } from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import getConfig from 'next/config';
import React from 'react';
import * as yup from 'yup';
import { Modal, Row, FormElements, Grid, Text } from '@custom-components';
import { analyticsPropTypes } from '@lib/commonPropTypes';
import withAnalytics from '../../../hocs/withAnalytics';
import withAuth, { withAuthPropType } from '../../../hocs/withAuth';
import { Content } from './styles';
import { REQUEST_ADMIN_TOKEN, QUERY_GET_IDENTITIES_FOR_ORGANIZATION } from './gql';

const {
  publicRuntimeConfig: { SUPPLIER_URI, CUSTOMER_URI }
} = getConfig();

function OrganizationEditModal(props) {
  const { data, closeModal, requestAdminToken, auth, organization, subSite, subTitle, type, analytics } = props;
  const sortOrgMembers = (_label) => {
    let sortOrder = 1;
    let label = _label;
    if (label[0] === '-') {
      sortOrder = -1;
      label = label.substr(1);
    }

    return (a, b) => {
      if (sortOrder === -1) {
        return b[label].localeCompare(a[label]);
      }
      return a[label].localeCompare(b[label]);
    };
  };
  const updateData = async () => {
    await data.refetch();
  };
  const getOrgMemberOptions = () => {
    updateData();
    const organizationMembers = get(data, 'AdminQueries.getIdentitiesForOrganization', []);
    const memberOptions = [];
    if (organizationMembers.length > 0 && type !== 'supplier') {
      organizationMembers.forEach((orgMember) => {
        memberOptions.push({ value: orgMember.id.toString(), label: `${orgMember.firstName} ${orgMember.lastName}` });
      });
    } else {
      memberOptions.push({ value: auth.me.identityId.toString(), label: 'Defaulting to Admin ID' });
    }
    memberOptions.sort(sortOrgMembers('label'));
    return memberOptions;
  };

  const validationSchema = () =>
    yup.object().shape({
      reason: yup.string().required('Must select a reason for editing'),
      action: yup.string().required('Must describe what action you will be taking'),
      orgMember: yup.string().required('Must select a member of this organization')
    });

  const openOrganization = (formDetails) => {
    /**
     * First, open a new window to avoid popup blockers, and then set the URL after the API call is successful.
     * Popup blockers activate when the browser thinks a new popup wasn't triggered by the user.
     * So, if we open a new tab after the API call, there's a chance the popup blocker will kick in.
     */
    const newWindow = window.open('', '_blank');
    requestAdminToken({
      variables: {
        targetOrganizationId: organization.id,
        action: formDetails.action,
        reason: formDetails.reason,
        identityOnBehalf: parseInt(formDetails.orgMember, 10)
      }
    }).then(() => {
      if (type === 'supplier') {
        newWindow.location.href = SUPPLIER_URI + subSite;
      } else if (type === 'customer') {
        newWindow.location.href = CUSTOMER_URI + subSite;
      }
      window.location.reload();
    });
    closeModal();

    analytics.trackEvent({
      event: 'track-event',
      eventName: 'openOrgEditor',
      orgEditorType: type
    });
  };

  const options = getOrgMemberOptions();

  return (
    <Formik
      initialValues={{
        action: '',
        reason: '',
        orgMember: ''
      }}
      onSubmit={openOrganization}
      validationSchema={validationSchema()}
    >
      {({ handleSubmit, handleChange, errors, values, submitCount, setFieldValue }) => (
        <Modal
          disableEnforceFocus
          openModal
          handleClose={closeModal}
          title={`OPEN ORGANIZATION EDITOR ${subTitle || ''}`}
          actions={[
            {
              label: 'CANCEL',
              onClick: closeModal,
              btnTheme: 'secondary'
            },
            {
              label: 'OPEN EDITOR',
              onClick: handleSubmit,
              btnTheme: 'primary'
            }
          ]}
        >
          <Content>
            {auth.me.adminToken && auth.me.adminToken.organization.id !== organization.id && (
              <Text.CopyParagraph>
                {`NOTE: You are currently editing "${auth.me.adminToken.organization.name}". If you open a new editor you will be
                    logged out of this session.`}
              </Text.CopyParagraph>
            )}
            <Text.CopyBody>
              {`You are about to open the "${organization.name}" organization to edit their details. Select a Reason, choose a member of the organization and add any detail in the Action before proceeding.`}
            </Text.CopyBody>
            <form>
              <Row>
                <Grid.Col lg={12}>
                  <FormElements.Select
                    onChange={handleChange}
                    name="reason"
                    id="reason"
                    label="Reason"
                    options={[
                      { label: 'By Request of Supplier', value: 'By Request of Supplier' },
                      { label: 'By Request of Customer', value: 'By Request of Customer' },
                      { label: 'Other', value: 'Other' }
                    ]}
                    value={values.reason}
                    error={submitCount > 0 && !!errors.reason}
                    errorText={errors.reason}
                  />
                </Grid.Col>
              </Row>
              <Row>
                <Grid.Col lg={12}>
                  <FormElements.TextInput
                    name="action"
                    id="action"
                    label="Action"
                    error={submitCount > 0 && !!errors.action}
                    errorText={errors.action}
                    value={values.action}
                    onChange={handleChange}
                    multiline
                    rows={4}
                  />
                </Grid.Col>
              </Row>
              <Row>
                <Grid.Col lg={12}>
                  <FormElements.SearchSelect
                    onChange={(option) => setFieldValue('orgMember', option?.target?.value || '')}
                    label="Organization Member"
                    options={options}
                    error={submitCount > 0 && !!errors.orgMember}
                    errorText={errors.orgMember}
                  />
                </Grid.Col>
              </Row>
            </form>
          </Content>
        </Modal>
      )}
    </Formik>
  );
}

OrganizationEditModal.propTypes = {
  data: PropTypes.shape({ refetch: PropTypes.func.isRequired }).isRequired,
  updateData: PropTypes.func, // eslint-disable-line react/no-unused-prop-types
  closeModal: PropTypes.func.isRequired,
  requestAdminToken: PropTypes.func.isRequired,
  organization: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    slug: PropTypes.string
  }).isRequired,
  auth: withAuthPropType.isRequired,
  refetch: PropTypes.func,
  subSite: PropTypes.string,
  subTitle: PropTypes.string,
  type: PropTypes.oneOf(['supplier', 'customer']),
  analytics: analyticsPropTypes.isRequired
};

OrganizationEditModal.defaultProps = {
  updateData: () => {},
  subSite: '',
  refetch: () => {},
  subTitle: '',
  type: 'supplier'
};

const ComposedOrganizationEditModal = compose(
  withAuth(),
  withAnalytics(),
  graphql(REQUEST_ADMIN_TOKEN, { name: 'requestAdminToken' }),
  graphql(QUERY_GET_IDENTITIES_FOR_ORGANIZATION, {
    options: (props) => {
      const { organization } = props;
      const organizationId = organization.id;
      return {
        variables: { organizationId }
      };
    }
  })
)(OrganizationEditModal);

export default ComposedOrganizationEditModal;
